import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { Doctor } from '../../../../shared/models/common';
import { ShilaDentistChoice, ShilaDentistStep, ShilaPo, ShilaPoLine, ShilaPoList } from '../../../../shared/models/shila-po.model';
import { doctorList } from '../../../../shared/ngrx/ngrx-selectors';
import { BroshService } from '../../../../shared/services/brosh.service';
import { RealtimeService } from '../../../../shared/services/realtime.service';
import { SharedService } from '../../../../shared/services/shared.service';

// One row per item of a PO. Shila sends a line per tooth, so eight crowns arrive as the same item eight times; the
// dialog shows the item once, with all eight teeth and the number of lines it stands for.
export interface ShilaPoItem {
  itemCode: string;
  itemDescr: string;
  teeth: string[];
  lineCount: number;
}

export function groupShilaPoLines(lines: ShilaPoLine[]): ShilaPoItem[] {
  const items = new Map<string, ShilaPoItem>();
  for (const line of lines) {
    const key = `${line.itemCode}\u0000${line.itemDescr}`;
    const item = items.get(key) ?? { itemCode: line.itemCode, itemDescr: line.itemDescr, teeth: [], lineCount: 0 };
    item.teeth.push(
      ...(line.teeth ?? '')
        .split(',')
        .map((tooth) => tooth.trim())
        .filter(Boolean)
    );
    item.lineCount++;
    items.set(key, item);
  }
  return [...items.values()];
}

// Everything the filter matches a PO on, lower-cased, a field per line so that a term never matches across two fields.
export function shilaPoFilterText(po: ShilaPo): string {
  const lines = (po.lines ?? []).flatMap((line) => [line.itemCode, line.itemDescr]);
  return [po.poNumber, po.patientName, po.patientFileNo, po.doctorName, ...lines].map((field) => (field ?? '').toLowerCase()).join('\n');
}

// The dentists the lab can pick for an unknown Shila dentist: the term narrows them by name, then the clinic's own
// come first and everyone else after, each group in Hebrew order.
export function groupDentists(doctors: Doctor[], clinicId: number, term: string): { atClinic: Doctor[]; others: Doctor[] } {
  const wanted = term.trim().toLowerCase();
  const byName = (a: Doctor, b: Doctor): number => (a.text ?? '').localeCompare(b.text ?? '', 'he');
  const matching = (doctors ?? []).filter((doctor) => (doctor.text ?? '').toLowerCase().includes(wanted));
  const atClinic = (doctor: Doctor): boolean => (doctor.clinicList ?? []).includes(String(clinicId));
  return {
    atClinic: matching.filter(atClinic).sort(byName),
    others: matching.filter((doctor) => !atClinic(doctor)).sort(byName),
  };
}

// What a dentist step asks, for telling whether a reload asks the same question; null when there is none.
export function dentistQuestion(step: ShilaDentistStep | null): string | null {
  return step ? JSON.stringify([step.outcome, step.shilaName, step.clinicId, (step.candidates ?? []).map((c) => c.doctorId)]) : null;
}

// Import Shila POs onto an order before approval, and release them. Spec:
// brosh_alg_services_v2 docs/superpowers/specs/2026-10-01-shila-purchase-orders-design.md.
@Component({
  selector: 'shila-po-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './shila-po-dialog.component.html',
  styleUrls: ['./shila-po-dialog.component.scss'],
})
export class ShilaPoDialogComponent implements OnInit, OnDestroy {
  @Input() orderId: number;
  @Output() changed = new EventEmitter<void>();

  list: ShilaPoList = { eligible: false, reason: null, pos: [], imported: [], dentist: null };
  // the grouped lines of every PO in the list, by PO number
  items = new Map<string, ShilaPoItem[]>();
  // The order's POs load once, unfiltered; the filter narrows the open ones here as the user types, in the server's
  // order. A selected PO the filter hides stays selected and is imported with the rest.
  filter = '';
  visiblePos: ShilaPo[] = [];
  private filterText = new Map<string, string>();
  selected = new Set<string>();
  busy = false;

  // The open dentist question of the order, if any. "Not now" hides it until the next load.
  dentistStep: ShilaDentistStep | null = null;
  dentistHidden = false;
  dentistMode: 'existing' | 'new' = 'existing';
  dentistFilter = '';
  pickedDoctorId: number | null = null;
  newDentist = { wholeName: '', phone: '', idNumber: '' };
  groups: { atClinic: Doctor[]; others: Doctor[] } = { atClinic: [], others: [] };
  private doctors = this.store.selectSignal(doctorList);

  constructor(
    private store: Store,
    private broshService: BroshService,
    private sharedService: SharedService,
    private translateService: TranslateService,
    private realtime: RealtimeService
  ) {}

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.load();
    // the on-prem box reported on this order, or on its Shila branch
    this.realtime.nudges$.pipe(takeUntil(this.destroy$)).subscribe((n) => {
      if (n.type !== 'SHILA_PO') return;
      const code = this.list.shilaClinicCode;
      if (n.orderIds?.includes(this.orderId) || (code && n.shilaClinicCodes?.includes(code))) {
        this.load(true);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // fromPush: the reload a SHILA_PO push asks for (the box's confirmation of this user's import, or anyone's change on
  // the branch). It keeps what the user picked or typed, and a "not now", while the dentist question is the same one.
  load(fromPush = false): void {
    this.broshService.getShilaPos(this.orderId).subscribe((list) => {
      const sameQuestion = fromPush && dentistQuestion(list.dentist) === dentistQuestion(this.dentistStep);
      this.list = list;
      this.items = new Map([...list.imported, ...list.pos].map((po) => [po.poNumber, groupShilaPoLines(po.lines ?? [])]));
      this.filterText = new Map(list.pos.map((po) => [po.poNumber, shilaPoFilterText(po)]));
      this.selected = new Set([...this.selected].filter((po) => list.pos.some((p) => p.poNumber === po)));
      this.filterBy(this.filter);
      this.dentistStep = list.dentist;
      if (!sameQuestion) {
        this.dentistHidden = false;
        // every question starts afresh, so a previous new dentist's phone and ID number are never sent with the next one
        this.dentistMode = 'existing';
        this.pickedDoctorId = null;
        this.dentistFilter = '';
        this.newDentist = { wholeName: list.dentist?.shilaName ?? '', phone: '', idNumber: '' };
      }
      this.groupDoctors();
    });
  }

  groupDoctors(): void {
    this.groups = this.dentistStep
      ? groupDentists(this.doctors(), this.dentistStep.clinicId, this.dentistFilter)
      : { atClinic: [], others: [] };
  }

  setDentistMode(mode: 'existing' | 'new'): void {
    this.dentistMode = mode;
    this.pickedDoctorId = null;
  }

  pickDentist(doctorId: number): void {
    this.pickedDoctorId = doctorId;
  }

  // A similar dentist picked in the new form means the lab chose an existing one: a mis-click must not create it anew.
  get canCreateDentist(): boolean {
    return !this.busy && this.pickedDoctorId === null && !!this.newDentist.wholeName.trim();
  }

  chooseDentist(): void {
    this.resolveDentist({ doctorId: this.pickedDoctorId });
  }

  createDentist(): void {
    const { wholeName, phone, idNumber } = this.newDentist;
    this.resolveDentist({
      newDentist: { wholeName: wholeName.trim(), phone: phone.trim() || undefined, idNumber: idNumber.trim() || undefined },
    });
  }

  private resolveDentist(choice: ShilaDentistChoice): void {
    this.busy = true;
    this.broshService.resolveShilaDentist(this.orderId, choice).subscribe(
      (rep) => {
        this.busy = false;
        this.sharedService.alertInfo(
          this.translateService.instant(rep.outcome === 'WAITING' ? 'translate.shilaPo.dentist.waiting' : 'translate.shilaPo.dentist.set')
        );
        this.load();
        this.changed.emit();
      },
      () => (this.busy = false)
    );
  }

  filterBy(filter: string): void {
    this.filter = filter;
    const term = filter.trim().toLowerCase();
    this.visiblePos = this.list.pos.filter((po) => this.filterText.get(po.poNumber).includes(term));
  }

  // how many of the selected POs the filter hides
  get hiddenSelected(): number {
    return [...this.selected].filter((po) => !this.visiblePos.some((p) => p.poNumber === po)).length;
  }

  toggle(poNumber: string): void {
    if (this.selected.has(poNumber)) {
      this.selected.delete(poNumber);
    } else {
      this.selected.add(poNumber);
    }
  }

  importSelected(): void {
    if (!this.selected.size) {
      return;
    }
    this.busy = true;
    this.broshService.importShilaPos(this.orderId, [...this.selected]).subscribe(
      (rep) => {
        this.busy = false;
        this.selected.clear();
        // UNKNOWN and SEVERAL are asked of the user by the dentist step, which load() brings up
        if (rep.dentist?.outcome === 'WAITING') {
          this.sharedService.alertInfo(this.translateService.instant('translate.shilaPo.dentist.waiting'));
        } else if (rep.dentist?.outcome === 'UNNAMED') {
          this.sharedService.alertInfo(this.translateService.instant('translate.shilaPo.doctorNotMatched'));
        }
        this.load();
        this.changed.emit();
      },
      () => (this.busy = false)
    );
  }

  release(poNumber: string): void {
    this.busy = true;
    this.broshService.releaseShilaPos(this.orderId, [poNumber]).subscribe(
      () => {
        this.busy = false;
        this.load();
        this.changed.emit();
      },
      () => (this.busy = false)
    );
  }
}
